import { ApiErrorCodeLabelModifier } from '@/api/error-codes';
import { Box, ErrorContent, Flex, Text, theme } from '@/components';
import { AnalysisResultsSkeletonContents } from '@/features/analysis/_components/AnalysisResultsSkeletonContents';
import { useHandle403ErrorWhenSearching as useHandle403ErrorWhenSearchingForPS } from '@/features/public-site/hooks';
import { isAxiosError } from 'axios';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import { useKsAnalysis } from '../_hooks/use-ks-analysis';
import { KsAnalysisResultTable } from './KsAnalysisResultTable';
import { KsAnalysisVisualizer } from './KsAnalysisVisualizer';

const KsAnalysisResultsError: FC<{ errorMessage: string }> = ({
  errorMessage,
}) => {
  const { token } = theme.useToken();
  return (
    <Flex
      style={{
        backgroundColor: token.colorBgLayout,
        height: 300,
        margin: 16,
      }}
      justify="center"
      align="center"
    >
      <Text text={errorMessage} />
    </Flex>
  );
};

export const KsAnalysisResults: FC = () => {
  const { t } = useTranslation();
  const { searchQueryStates } = useFundSheetAnalysisPageSearchQueryStates();

  const ksAnalysis = useKsAnalysis({
    params: {
      fiscalYear: searchQueryStates.fiscalYear,
      projectNumbers: searchQueryStates.projectNumbers,
      q: searchQueryStates.q,
      textSearchCriteria: searchQueryStates.isSearchTargetProjectName
        ? 'name'
        : null,
      cascader: searchQueryStates.cascader,
      // 見える化サイト(PS)では cascader の代わりに organizationIds(leaf id) で絞り込む（RS-4451）。
      organizationIds: searchQueryStates.organizationIds?.join(',') ?? null,
      corporateNumber: searchQueryStates.corporateNumber,
      // `total` は合計のみ（軸なし）の URL センチネル。API には null を渡す（RS-4424）。
      analysisAxis:
        searchQueryStates.analysisAxis === 'total'
          ? null
          : searchQueryStates.analysisAxis,
      aggregationTargetType: searchQueryStates.aggregationTargetType,
    },
  });

  useHandle403ErrorWhenSearchingForPS(ksAnalysis);

  if (ksAnalysis.isError) {
    const errorCode = isAxiosError(ksAnalysis.error)
      ? ksAnalysis.error.response?.data.error_code
      : undefined;
    const message = errorCode ? ApiErrorCodeLabelModifier[errorCode] : null;
    if (message) {
      return <KsAnalysisResultsError errorMessage={message} />;
    }
    return (
      <Box marginTop={16}>
        <ErrorContent reloadable={false} />
      </Box>
    );
  }

  if (ksAnalysis.isPending) {
    return <AnalysisResultsSkeletonContents />;
  }

  return ksAnalysis.data?.length ? (
    <Flex vertical gap={16}>
      <KsAnalysisVisualizer graphData={ksAnalysis.data} />
      <KsAnalysisResultTable data={ksAnalysis.data} />
    </Flex>
  ) : (
    <KsAnalysisResultsError errorMessage={t('no-data')} />
  );
};
